import { useEffect, useMemo, useState } from "preact/hooks";
import type { CoreData, CountryId, NameDetailData, NameEntry, Series, SexDetail, SexIdx, Verdict } from "../data/types";
import { useStore } from "../state/store";
import { decide, decisions, haptic, listByVerdict, settings, shareOrCopy, siteOrigin, toast, ui } from "../state/app";
import { checkInstantMatch, partnerName, partnerSets } from "../state/partner";
import { closeOverlay, openName } from "../state/router";
import { entryFor, loadDetail } from "../data/load";
import { swipeWith } from "../state/deck";
import { listWords } from "../data/origins";
import { nameKey } from "../data/keys";
import {
  SEX_WORD,
  awkwardInitials,
  classChance,
  percent,
  fmt,
  genderClass,
  genderLabel,
  initialsOf,
  oneIn,
  ordinal,
  rankChange,
  sexIdxFor,
  trendInfo,
} from "../data/format";
import { Sheet } from "./Sheet";
import { HistoryChart, type ChartSeries } from "./charts";
import { Segmented } from "./FilterSheet";
import { cheer, tally } from "./cheer";
import { tagChips } from "./NameCard";
import { IconArrow, IconHeart, IconShare, IconSpeaker, IconStar, IconX } from "./icons";
import { canSpeak, speak } from "./speak";
import { TAGLINE, shareNameCard } from "./shareImage";
import { ChooseButton } from "./Chosen";
import { NameStory } from "./NameStory";
import { generation } from "../data/analysis";

const DECADES = [1904, 1914, 1924, 1934, 1944, 1954, 1964, 1974, 1984, 1994, 2004, 2014, 2024];

const SOURCE_LINE: Record<CountryId, string> = {
  uk: "Counts: ONS (England & Wales), National Records of Scotland, NISRA. Open Government Licence v3.0. UK ranks combine all three.",
  us: "Counts: US Social Security Administration, from Social Security card applications; names given to 5 or more babies a year. Ranks are computed by Nameling.",
  other:
    "Counts: the UK's official records (ONS, National Records of Scotland, NISRA; Open Government Licence v3.0) and the US Social Security Administration's, added together from 1996. Ranks are computed by Nameling.",
};

function seriesValues(years: number[], s: Series | undefined, zeroFill: boolean): (number | null)[] {
  return years.map((y) => {
    if (!s) return zeroFill ? 0 : null;
    const i = y - s[0] + 1;
    if (i >= 1 && i < s.length) return s[i] || (zeroFill ? 0 : null);
    return zeroFill ? 0 : null;
  });
}

function sumSeries(s?: Series): number {
  return s ? s.slice(1).reduce((a, b) => a + b, 0) : 0;
}

function DecisionBar({ k, name, inDeck }: { k: string; name: string; inDeck: boolean }) {
  const v = useStore(decisions, (s) => s.map[k]?.d);
  const set = (d: Verdict, button: HTMLElement) => {
    const before = tally();
    decide(k, v === d ? null : d, undefined, inDeck ? undefined : name);
    if (v === d) return;
    if (d > 0 && checkInstantMatch(k)) {
      haptic([30, 50, 30, 50, 60]);
      ui.set({ match: k });
      cheer(d, button, before, { quiet: true, fly: false });
    } else if (!cheer(d, button, before, { fly: false })) toast(d === -1 ? `Passed on ${name}` : d === 2 ? `Loved ${name}` : `Liked ${name}`);
  };
  return (
    <div class="decision-bar" role="group" aria-label={`Your verdict on ${name}`}>
      <button class={`dec dec-nope ${v === -1 ? "on" : ""}`} aria-pressed={v === -1} onClick={(ev) => set(-1, ev.currentTarget)}>
        <IconX size={18} /> Nope
      </button>
      <button class={`dec dec-like ${v === 1 ? "on" : ""}`} aria-pressed={v === 1} onClick={(ev) => set(1, ev.currentTarget)}>
        <IconHeart size={18} filled={v === 1} /> Like
      </button>
      <button class={`dec dec-love ${v === 2 ? "on" : ""}`} aria-pressed={v === 2} onClick={(ev) => set(2, ev.currentTarget)}>
        <IconStar size={18} filled={v === 2} /> Love
      </button>
    </div>
  );
}

function FullNameBuilder({ first, core, si }: { first: string; core: CoreData; si: SexIdx }) {
  const surname = useStore(settings, (s) => s.surname);
  const map = useStore(decisions, (s) => s.map);
  const [middle, setMiddle] = useState("");
  // middle names from the shortlist, for the same sex as this name
  const suggestions = useMemo(
    () =>
      listByVerdict(map, [2, 1])
        .map((k) => core.byKey.get(k))
        .filter((e): e is NameEntry => !!e && e.name !== first && (e.g === "B" || e.g === (si === 0 ? "F" : "M")))
        .slice(0, 10)
        .map((e) => e.name),
    [map, first, si],
  );
  const full = [first, middle.trim(), surname.trim()].filter(Boolean).join(" ");
  const initials = initialsOf(first, middle, surname);
  const notes: string[] = [];
  if (initials.length >= 2 && awkwardInitials(initials)) notes.push(`The initials spell “${initials}”.`);
  const vowel = /[aeiouy]$/i;
  if (surname.trim() && vowel.test(first) && /^[aeiou]/i.test(surname.trim()))
    notes.push(`${first} ends and ${surname.trim()} starts with a vowel, so they may run together when said aloud.`);
  if (surname.trim() && first.charAt(0).toUpperCase() === surname.trim().charAt(0).toUpperCase())
    notes.push("The first name and surname start with the same letter: some people love that, some don't.");
  if (surname.trim() && first.toLowerCase().slice(-2) === surname.trim().toLowerCase().slice(-2))
    notes.push("First name and surname rhyme or end the same way.");

  return (
    <section class="dsec">
      <h2>Try it out</h2>
      <div class="fullname-card">
        <p class="fullname">{full}</p>
        {initials.length > 1 && (
          <p class="initials">
            Initials <b>{initials.split("").join(".")}.</b>
          </p>
        )}
        {notes.map((n) => (
          <p class="note" key={n}>
            {n}
          </p>
        ))}
      </div>
      <label class="field">
        <span>Surname</span>
        <input
          type="text"
          value={surname}
          placeholder="Stays on this phone"
          maxLength={40}
          autocomplete="family-name"
          onInput={(ev) => settings.set({ surname: (ev.target as HTMLInputElement).value })}
        />
      </label>
      <label class="field">
        <span>Middle name</span>
        <input
          type="text"
          value={middle}
          placeholder="Try a middle name"
          maxLength={30}
          onInput={(ev) => setMiddle((ev.target as HTMLInputElement).value)}
        />
      </label>
      {suggestions.length > 0 && (
        <div class="chips small" role="group" aria-label="Middle names from your shortlist">
          {suggestions.map((n) => (
            <button key={n} class={`chip-btn ${middle === n ? "on" : ""}`} onClick={() => setMiddle(middle === n ? "" : n)}>
              {n}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function DetailBody({ k, core }: { k: string; core: CoreData }) {
  const entry: NameEntry | undefined = core.byKey.get(k);
  const sexPref = useStore(settings, (s) => s.sex);
  const pLiked = useStore(partnerSets, (s) => s.liked.has(k));
  const pLoved = useStore(partnerSets, (s) => s.loved.has(k));
  const hasPartner = useStore(partnerSets, (s) => s.others.length > 0);
  const myVerdict = useStore(decisions, (s) => s.map[k]?.d ?? 0);
  // blind voting: only reveal a partner's like once it's a match, unless hints are on
  const hints = useStore(settings, (s) => s.partnerHints);
  const [data, setData] = useState<NameDetailData | null | undefined>(undefined);
  const [mode, setMode] = useState<"count" | "rank">("count");
  const [table, setTable] = useState(false);
  const [compareKey, setCompareKey] = useState<string | null>(null);
  const [compareData, setCompareData] = useState<NameDetailData | null>(null);
  const shortlistMap = useStore(decisions, (s) => s.map);

  useEffect(() => {
    let live = true;
    setData(undefined);
    loadDetail(k)
      .then((d) => live && setData(d))
      .catch(() => live && setData(null));
    return () => {
      live = false;
    };
  }, [k]);

  useEffect(() => {
    let live = true;
    setCompareData(null);
    if (compareKey) loadDetail(compareKey).then((d) => live && setCompareData(d));
    return () => {
      live = false;
    };
  }, [compareKey]);

  const country = core.country;
  // the chart covers the whole record: since 1996 for the UK, 1880 for the US
  const from = country.since;
  const years = useMemo(() => Array.from({ length: core.latest - from + 1 }, (_, i) => from + i), [core, from]);
  const name = entry?.name ?? data?.n ?? k;
  const si: SexIdx = entry ? sexIdxFor(entry, sexPref) : data?.F?.t && !data?.M?.t ? 0 : data?.M?.t ? 1 : 0;
  const sexes: SexIdx[] = useMemo(() => {
    if (entry) return entry.g === "B" ? [0, 1] : [entry.g === "F" ? 0 : 1];
    const out: SexIdx[] = [];
    if (data?.F?.t) out.push(0);
    if (data?.M?.t) out.push(1);
    return out.length ? out : [0];
  }, [entry, data]);

  const detailFor = (s: SexIdx): SexDetail | undefined => (s === 0 ? data?.F : data?.M);
  const series: ChartSeries[] = sexes.map((s) => {
    const d = detailFor(s);
    return {
      id: s === 0 ? "F" : "M",
      label: SEX_WORD[s],
      cls: s === 0 ? "s-girl" : "s-boy",
      values: seriesValues(years, mode === "count" ? d?.t : d?.rk, mode === "count"),
    };
  });

  // compare against a shortlisted name: the focal name keeps its colour, the other is muted
  const compareEntry = compareKey ? core.byKey.get(compareKey) : undefined;
  const compareOptions = useMemo(
    () =>
      listByVerdict(shortlistMap, [2, 1])
        .filter((ck) => ck !== k)
        .map((ck) => core.byKey.get(ck))
        .filter((e): e is NameEntry => !!e && (sexes.includes(0) ? e.g !== "M" : e.g !== "F"))
        .slice(0, 12),
    [shortlistMap, k, sexes],
  );
  if (compareKey && compareData && sexes.length) {
    const s0 = sexes.length > 1 ? si : sexes[0];
    const focal = series.find((x) => x.id === (s0 === 0 ? "F" : "M"));
    const cd = s0 === 0 ? compareData.F : compareData.M;
    series.length = 0;
    if (focal) series.push({ ...focal, label: name });
    series.push({
      id: "cmp",
      label: compareEntry?.name ?? compareData.n,
      cls: "s-muted",
      values: seriesValues(years, mode === "count" ? cd?.t : cd?.rk, mode === "count"),
    });
  }

  const d0 = detailFor(si);
  const like = (d0?.like ?? []).filter((x) => core.byKey.has(x));
  // which of them are already on the shortlist (a string, so the store can compare it)
  const likedKeys = useStore(decisions, (st) => like.filter((x) => (st.map[x]?.d ?? 0) > 0).join(","));
  const likedSet = new Set(likedKeys ? likedKeys.split(",") : []);
  const chance = entry ? classChance(core, entry, si) : null;
  // the middle half of its babies, shaded on the chart and told under it
  const gen = useMemo(() => generation(d0?.t, country.since), [d0, country]);
  const totalSince = sexes.reduce<number>((a, s) => a + sumSeries(detailFor(s)?.t), 0);
  const trend = entry ? trendInfo(entry.t[si]) : null;
  const moved = entry ? rankChange(entry, si) : null;
  const ratio = entry ? oneIn(core, entry, si) : null;
  const g = entry ? genderClass(entry) : si === 0 ? "girl" : "boy";
  const chips = entry ? tagChips(entry) : [];
  const [shareOpen, setShareOpen] = useState(false);
  const surname = useStore(settings, (s) => s.surname);
  const shareLink = async () => {
    setShareOpen(false);
    const text = `What do you think of ${name}?`;
    if (__PREVIEW__) {
      // the preview's own address isn't one anyone else can open, so share the facts instead
      const facts = entry?.r[si] ? ` It was #${fmt(entry.r[si])} for ${SEX_WORD[si]} in ${country.name} in ${core.latest}.` : "";
      const said = entry?.meaning ? ` It means “${entry.meaning}”.` : "";
      await shareOrCopy({ title: name, text }, `${text}${facts}${said}`, "Copied, ready to paste");
      return;
    }
    // the website's page for the name gives a proper link preview, and opens the app where it's
    // installed; names too rare for the deck have no page, so those open the app's own
    const url = entry
      ? `${siteOrigin()}/names/${encodeURIComponent(k)}`
      : `${siteOrigin()}/#/explore/name/${encodeURIComponent(k)}`;
    await shareOrCopy({ title: name, text, url }, `${text} ${url}`, "Link copied");
  };
  const sharePicture = async () => {
    setShareOpen(false);
    const facts: string[] = [];
    if (entry?.r[si]) facts.push(`#${fmt(entry.r[si])} for ${SEX_WORD[si]} in ${country.name}, ${core.latest}`);
    if (entry?.c[si]) facts.push(`${fmt(entry.c[si])} babies named ${name} that year`);
    if (trend && trend.dir !== "none") facts.push(`${trend.label} over five years`);
    try {
      const how = await shareNameCard(
        {
          name,
          gender: g,
          label: entry ? genderLabel(entry) : si === 0 ? "Girl" : "Boy",
          fullName: surname.trim() ? `${name} ${surname.trim()}` : undefined,
          meaning: data?.mean?.meaning ?? entry?.meaning ?? undefined,
          facts,
          tagline: TAGLINE[country.id],
          site: siteOrigin(),
        },
        `What do you think of ${name}?`,
      );
      if (how === "saved") toast("Picture saved");
    } catch {
      /* cancelled */
    }
  };

  const hist = d0?.hist ?? [];
  const early = d0?.early;
  const earlyRegion = early ? country.regions.find((r) => r.years[0] === early[0] || r.years[0] < country.since) : undefined;

  return (
    <div class={`detail g-${g}`}>
      <div class="detail-hero">
        <div class="detail-pills">
          <span class="gender-pill">
            <i class="gdot" aria-hidden="true" />
            {entry ? genderLabel(entry) : si === 0 ? "Girl" : "Boy"}
          </span>
          {chips.map((c) => (
            <span class={`chip ${c.cls}`} key={c.label}>
              {c.label}
            </span>
          ))}
        </div>
        <div class="detail-name">
          <h1 id="detail-name">{name}</h1>
          {canSpeak() && (
            <button
              class="icon-btn say-btn"
              onClick={() => speak(name, data?.mean?.pron || entry?.pron)}
              aria-label={`Hear ${name} said aloud`}
              title="Hear it"
            >
              <IconSpeaker size={22} />
            </button>
          )}
        </div>
        {hasPartner && pLiked && (myVerdict > 0 || hints) && (
          <p class="partner-badge">
            <IconHeart size={14} filled /> {partnerName()} {pLoved ? "loves" : "likes"} this
          </p>
        )}
        <div class="detail-hero-actions">
          <DecisionBar k={k} name={name} inDeck={!!entry} />
          <button
            class="icon-btn"
            onClick={() => setShareOpen(true)}
            aria-label={`Share ${name}`}
          >
            <IconShare size={20} />
          </button>
          <Sheet open={shareOpen} onClose={() => setShareOpen(false)} title={`Share ${name}`} size="auto">
            <div class="action-list">
              <button onClick={shareLink}>
                <IconShare size={20} /> {__PREVIEW__ ? "Copy a summary" : "Send a link"}
              </button>
              <button onClick={sharePicture}>
                <IconShare size={20} /> {__PREVIEW__ ? "Save as a picture" : "Share as a picture"}
              </button>
            </div>
          </Sheet>
        </div>
        {myVerdict > 0 && <ChooseButton k={k} name={name} />}
      </div>

      {data?.mean || entry?.meaning ? (
        <section class="dsec meaning">
          <h2>Meaning & origin</h2>
          {data?.mean?.origin || entry?.origin ? (
            <p class="origin">
              {data?.mean?.origin ?? entry?.origin}
              {data?.mean?.origin_detail && <span class="origin-detail"> · {data.mean.origin_detail}</span>}
            </p>
          ) : (
            // no single source language ("short form of names such as Antonina"): the phrase alone
            data?.mean?.origin_detail && (
              <p class="origin">
                <span class="origin-detail">{data.mean.origin_detail[0].toUpperCase() + data.mean.origin_detail.slice(1)}</span>
              </p>
            )
          )}
          {(data?.mean?.meaning || entry?.meaning) && <p class="meaning-big">“{data?.mean?.meaning ?? entry?.meaning}”</p>}
          {data?.mean?.text && <p class="meaning-text">{data.mean.text}</p>}
          {(data?.mean?.pron || (data?.mean?.nicknames && data.mean.nicknames.length > 0)) && (
            <dl class="meaning-facts">
              {data?.mean?.pron && (
                <>
                  <dt>Say it</dt>
                  <dd class="pron">{data.mean.pron}</dd>
                </>
              )}
              {data?.mean?.nicknames && data.mean.nicknames.length > 0 && (
                <>
                  <dt>Nicknames</dt>
                  <dd>{data.mean.nicknames.join(", ")}</dd>
                </>
              )}
            </dl>
          )}
          {(data?.mean?.of || data?.mean?.source === "wiktionary" || data?.mean?.wkt) && (
          <p class="source-note">
            {data?.mean?.of && (
              <>
                {data.mean.of.length > 1 ? "Meanings" : "Meaning"} borrowed from {listWords(data.mean.of, 4)}.{" "}
              </>
            )}
            {data?.mean?.source === "wiktionary" ? (
              <>
                From{" "}
                <a href={data.mean.url ?? `https://en.wiktionary.org/wiki/${encodeURIComponent(name)}`} target="_blank" rel="noopener">
                  Wiktionary
                </a>
                , CC BY-SA 4.0
              </>
            ) : (
              data?.mean?.wkt && (
                <>
                  More on{" "}
                  <a href={data.mean.wkt} target="_blank" rel="noopener">
                    Wiktionary
                  </a>
                  .
                </>
              )
            )}
          </p>
          )}
        </section>
      ) : null}

      <section class="tiles-card" aria-label="Key numbers">
        <div class="tiles">
          <div class="tile">
            <span class="tile-label">
              {country.short} rank, {core.latest}
            </span>
            <b class="tile-value">{entry?.r[si] ? `#${fmt(entry.r[si])}` : "—"}</b>
            <span class="tile-sub">among {SEX_WORD[si]}</span>
          </div>
          <div class="tile">
            <span class="tile-label">Babies, {core.latest}</span>
            <b class="tile-value">{entry?.c[si] ? fmt(entry.c[si]) : `Fewer than ${country.minListed}`}</b>
            <span class="tile-sub">{ratio ? `1 in ${ratio} ${SEX_WORD[si]}` : "too few to rank"}</span>
          </div>
          <div class="tile">
            <span class="tile-label">Five-year trend</span>
            <b class={`tile-value t-${trend?.dir ?? "none"}`}>
              {trend && trend.dir !== "none" && <IconArrow dir={trend.dir} size={18} />}
              {trend?.label ?? "—"}
            </b>
            <span class="tile-sub">
              {moved ? `${moved > 0 ? "Up" : "Down"} ${fmt(Math.abs(moved))} place${Math.abs(moved) === 1 ? "" : "s"}` : entry?.t[si] != null ? `${entry.t[si]! > 0 ? "+" : ""}${Math.min(entry.t[si]!, 999)}% babies` : " "}
            </span>
          </div>
          <div class="tile">
            <span class="tile-label">Same name in a class</span>
            <b class="tile-value">{chance !== null ? percent(chance) : "—"}</b>
            <span class="tile-sub">chance among 15 {SEX_WORD[si]}</span>
          </div>
          <div class="tile">
            <span class="tile-label">Peak since {country.since}</span>
            <b class="tile-value">{entry?.peakRank ? `#${fmt(entry.peakRank)}` : "—"}</b>
            <span class="tile-sub">{entry ? `in ${entry.peakYear}` : " "}</span>
          </div>
        </div>
      </section>

      <section class="dsec">
        <div class="dsec-head">
          <h2>{mode === "count" ? "Babies given this name each year" : `${country.short} rank each year`}</h2>
          <Segmented<"count" | "rank">
            label="Chart measure"
            value={mode}
            onChange={setMode}
            options={[
              { v: "count", label: "Babies" },
              { v: "rank", label: "Rank" },
            ]}
          />
        </div>
        {compareOptions.length > 0 && (
          <div class="compare-row">
            <span class="compare-label">Compare with</span>
            <div class="chips small" role="group" aria-label="Compare with a name from your shortlist">
              {compareOptions.map((e) => (
                <button
                  key={e.key}
                  class={`chip-btn ${compareKey === e.key ? "on" : ""}`}
                  aria-pressed={compareKey === e.key}
                  onClick={() => setCompareKey(compareKey === e.key ? null : e.key)}
                >
                  {e.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div class={`chart-frame ${data === undefined || (compareKey && !compareData) ? "loading" : ""}`}>
          {data === null ? (
            <p class="muted">Couldn't load the history for this name.{__NATIVE__ ? "" : " Check your connection."}</p>
          ) : (
            <HistoryChart
              years={years}
              series={series}
              mode={mode}
              rankLabel={`${country.short} rank`}
              band={gen && !gen.before && !compareKey && sexes.length === 1 ? { from: gen.from, to: gen.to } : undefined}
            />
          )}
        </div>
        <div class="chart-foot">
          <span class="muted small">
            {fmt(totalSince)} babies since {from}
            {early && earlyRegion ? ` · in ${earlyRegion.name}'s records since ${early[0]}` : ""}
          </span>
          <button class="link-btn" onClick={() => setTable(!table)} aria-expanded={table}>
            {table ? "Hide table" : "Show as table"}
          </button>
        </div>
        {table && (
          <div class="table-wrap" tabIndex={0} role="region" aria-label={`${name}: babies and rank by year`}>
            <table class="data-table">
              <thead>
                <tr>
                  <th scope="col">Year</th>
                  {sexes.map((s) => (
                    <th scope="col" key={`c${s}`}>
                      Babies ({SEX_WORD[s]})
                    </th>
                  ))}
                  {sexes.map((s) => (
                    <th scope="col" key={`r${s}`}>
                      Rank ({SEX_WORD[s]})
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {years
                  .slice()
                  .reverse()
                  .map((y) => (
                    <tr key={y}>
                      <th scope="row">{y}</th>
                      {sexes.map((s) => {
                        const v = seriesValues([y], detailFor(s)?.t, true)[0];
                        return <td key={`c${s}`}>{v ? fmt(v) : "–"}</td>;
                      })}
                      {sexes.map((s) => {
                        const v = seriesValues([y], detailFor(s)?.rk, false)[0];
                        return <td key={`r${s}`}>{v ? `#${fmt(v)}` : "–"}</td>;
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <NameStory
        core={core}
        k={k}
        name={name}
        entry={entry}
        data={data}
        si={si}
        gen={gen}
        shaded={!!gen && !gen.before && sexes.length === 1}
      />

      {d0 && country.regions.length > 0 && country.regions.some((r) => d0[r.id as keyof SexDetail]) && (
        <section class="dsec">
          <h2>
            Across {country.name} in {core.latest}
          </h2>
          <table class="data-table nations">
            <thead>
              <tr>
                <th scope="col">{country.id === "other" ? "Country" : "Nation"}</th>
                <th scope="col">Babies</th>
                <th scope="col">Rank</th>
              </tr>
            </thead>
            <tbody>
              {country.regions.map((r) => {
                const v = seriesValues([core.latest], d0[r.id as "ew"] as Series | undefined, true)[0] ?? 0;
                const rk = d0.nr?.[r.id];
                return (
                  <tr key={r.id}>
                    <th scope="row">{r.name}</th>
                    <td>{v ? fmt(v) : "–"}</td>
                    <td>{v && rk ? `#${fmt(rk)}` : "–"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p class="source-note">
            {country.id === "other"
              ? "The UK lists names given to 3 or more babies a year and the US 5 or more, so very rare names can show “–”."
              : "England & Wales and Northern Ireland only list names given to 3 or more babies, so very rare names can show “–” there."}
          </p>
        </section>
      )}

      {hist.length > 0 && (
        <section class="dsec">
          <h2>A century ago</h2>
          <p class="muted small">
            {country.id === "uk" ? "England and Wales" : country.id === "other" ? "England and Wales or US" : country.short} top
            100, every ten years from 1904 to{" "}
            {DECADES[DECADES.length - 1]}.
          </p>
          <div class="decades">
            {DECADES.map((y) => {
              const h = hist.find(([yy]) => yy === y);
              return (
                <div class={`decade ${h ? "in" : ""}`} key={y}>
                  <span>{y}</span>
                  <b>
                    {h ? (
                      ordinal(h[1])
                    ) : (
                      <>
                        <span aria-hidden="true">–</span>
                        <span class="sr-only">not in the top 100</span>
                      </>
                    )}
                  </b>
                </div>
              );
            })}
          </div>
        </section>
      )}


      {data?.var && data.var.length > 0 && (
        <section class="dsec">
          <h2>Other spellings</h2>
          <div class="chips">
            {data.var.map((v) => (
              <button key={v} class="chip-btn" onClick={() => openName(nameKey(v))}>
                {v}
              </button>
            ))}
          </div>
        </section>
      )}

      {like.length > 0 && (
        <section class="dsec names-like" aria-labelledby="like-title">
          <h2 id="like-title">Names like {name}</h2>
          <p class="muted small">From the records: names used in the same years and the same naming tradition, with a similar sound.</p>
          <div class="chips">
            {like.map((x) => (
              <button key={x} class={`chip-btn${likedSet.has(x) ? " liked" : ""}`} onClick={() => openName(x)}>
                {entryFor(core, x).name}
                {likedSet.has(x) && <span class="sr-only"> (on your shortlist)</span>}
              </button>
            ))}
          </div>
          <button
            class="btn btn-ghost btn-small like-swipe"
            onClick={() => swipeWith({ list: { title: `Names like ${name}`, keys: like, country: core.country.id } })}
          >
            Swipe names like {name}
          </button>
        </section>
      )}

      <FullNameBuilder first={name} core={core} si={si} />

      <p class="source-note detail-source">{SOURCE_LINE[country.id]}</p>
    </div>
  );
}

export function NameDetailSheet({ core, k }: { core: CoreData; k: string | null }) {
  // keep showing the last name while the sheet animates closed
  const [last, setLast] = useState(k);
  useEffect(() => {
    if (k) setLast(k);
  }, [k]);
  const shown = k ?? last;
  return (
    <Sheet
      open={!!k}
      onClose={closeOverlay}
      title={shown ? core.byKey.get(shown)?.name ?? "Name" : "Name"}
      labelledBy="detail-name"
      size="full"
      class="detail-sheet"
    >
      {shown && <DetailBody k={shown} core={core} key={shown} />}
    </Sheet>
  );
}
